/**
 * Typed wrapper around `queryPages('settings')`.
 *
 * Centralises the single `as` cast at the Nuxt-Content boundary so callers
 * see `Settings | null` everywhere. Pair with `useAsyncData` in callers that
 * need reactivity — this helper is just the inner fetch.
 */
import type { Settings, ArchiveDeclaration, VocabEntry } from '~~/content.config'
import { queryPages } from '~/utils/contentQueries'

export async function querySettings(locale: string): Promise<Settings | null> {
  if (!locale) return null
  const row = await (await queryPages('settings')).path(`/${locale}/settings`).first()
  const settings = (row ?? null) as Settings | null
  if (!settings?.archives) return settings
  // Nuxt Content does not persist the schema's `.default([])` for the nested
  // `categories`/`tags` arrays when a `blog`-style archive decl omits them, so
  // they arrive `undefined`. Every downstream consumer (ArchivePage,
  // ArchiveFilterBar, ArticleCard) reads `.length`/`.find` on them directly —
  // normalise once here, the single boundary all archive reads flow through.
  for (const archive of settings.archives as Array<
    ArchiveDeclaration & { categories?: VocabEntry[]; tags?: VocabEntry[] }
  >) {
    archive.categories ??= []
    archive.tags ??= []
  }
  return settings
}
